// Helper murni untuk daftar pelanggan admin (/admin/customers).
//
// Halaman pelanggan kini PAGINASI di sisi server (20 per halaman, infinite
// scroll) — bukan lagi memuat semua pelanggan sekaligus. Filter tab & pencarian
// juga dievaluasi di server. Modul ini menjadi SUMBER TUNGGAL definisi tab,
// sanitasi pencarian, dan penerapan filter ke query builder Supabase, sehingga
// SSR halaman, fetch paginasi client, query hitung jumlah, dan query "pilih
// semua" memakai logika yang identik. Pure & diuji (`customers.test.ts`).

// Kunci tab per-grup dibentuk `group:<id>` (bukan union tetap) karena tabnya
// SENDIRI dinamis — satu tab per grup pelanggan aktif, bukan satu tab
// "Reseller" yang menggantung ke satu istilah (lihat `buildCustomerFilterTabs`).
export type CustomerFilterKey =
  | "all"
  | "pending"
  | "no-group"
  | "inactive"
  | `group:${string}`;

/** Jumlah pelanggan yang dimuat per halaman (default tampilan). */
export const CUSTOMERS_PAGE_SIZE = 20;

const GROUP_FILTER_PREFIX = "group:";

/** Bentuk kunci filter tab untuk satu grup pelanggan. */
export function customerGroupFilterKey(groupId: string): CustomerFilterKey {
  return `${GROUP_FILTER_PREFIX}${groupId}`;
}

function groupIdFromFilterKey(filter: CustomerFilterKey): string | null {
  return filter.startsWith(GROUP_FILTER_PREFIX)
    ? filter.slice(GROUP_FILTER_PREFIX.length)
    : null;
}

export type CustomerFilterTab = { key: CustomerFilterKey; label: string };

/** Kunci filter yang selalu ada, di luar tab per-grup (dipakai test + SSR awal). */
export const STATIC_CUSTOMER_FILTER_KEYS: CustomerFilterKey[] = [
  "all",
  "pending",
  "no-group",
  "inactive"
];

/**
 * Bangun daftar tab filter /admin/customers. Tab tunggal "Reseller" (lama:
 * `with-group`, cakupan "punya grup apa saja") DIGANTI satu tab PER GRUP
 * PELANGGAN AKTIF, berlabel nama grup itu sendiri persis seperti yang
 * dikonfigurasi di halaman Grup Pelanggan (mis. "Reseller", "Distributor") —
 * bukan digantung ke satu istilah tetap. Grup yang sudah dinonaktifkan tidak
 * dapat tab sendiri (pelanggannya tetap kelihatan di tab "Semua").
 */
export function buildCustomerFilterTabs(
  groups: { id: string; name: string; isActive: boolean }[]
): CustomerFilterTab[] {
  const groupTabs = groups
    .filter((g) => g.isActive)
    .map((g) => ({ key: customerGroupFilterKey(g.id), label: g.name }));
  return [
    { key: "all", label: "Semua" },
    { key: "pending", label: "Pending" },
    ...groupTabs,
    { key: "no-group", label: "Customer" },
    { key: "inactive", label: "Nonaktif" }
  ];
}

export type CustomerCounts = Record<string, number>;

/** Belum ada tab yang dihitung (mis. sebelum grup termuat). */
export const EMPTY_CUSTOMER_COUNTS: CustomerCounts = {};

/**
 * Kolom view `customer_summary` yang benar-benar dibaca `mapCustomerSummaryRow`
 * (+ `mapCustomer` di AdminCustomerEdit yang bentuknya sama). Dipakai sebagai
 * pengganti `select("*")` — view-nya ikut membawa kolom lain yang tak pernah
 * dibaca (PF-15 audit 2026-08).
 *
 * ⛔ RUMAHNYA DI SINI, JANGAN DIPINDAH KE `supabase/data.ts`. Modul itu
 * ber-`"use client"`, dan SSR `/admin/customers/page.tsx` (Server Component)
 * ikut memakai konstanta ini. Ekspor modul client yang diimpor Server Component
 * BUKAN nilainya, melainkan client-reference proxy bikinan Next.js — sebuah
 * FUNGSI yang melempar saat dipanggil. `.select(<fungsi>)` membuat postgrest-js
 * menjalankan `columns.split(...)` → `TypeError: ….split is not a function`,
 * dilempar SINKRON sebelum request pertama berangkat, sehingga SELURUH halaman
 * gagal render (500) — bukan sekadar datanya kosong. Persis itu yang terjadi
 * saat konstanta ini sempat tinggal di `data.ts` (regresi PF-15, 2026-08-13):
 * `/admin/customers` tak bisa dibuka sama sekali, sementara log Supabase bersih
 * 200 semua karena request-nya memang tak pernah terkirim. Idiom yang sama
 * dengan `poDetail.ts` yang dulu diekstrak dari `board.ts` ber-`"use client"`.
 *
 * (Satu baris literal — konkatenasi membuat TS kehilangan literal type dan
 * parser supabase-js mengetik barisnya GenericStringError.)
 */
// prettier-ignore
export const CUSTOMER_SUMMARY_COLUMNS =
  "customer_id, full_name, store_name, phone, email, status, customer_level, group_id, group_name, assigned_admin_id, assigned_admin_name, created_at, total_order, total_belanja, last_order_at";

/**
 * Bersihkan input pencarian agar aman dipakai dalam ekspresi PostgREST `or`
 * (yang memakai koma sebagai pemisah kondisi & tanda kurung sebagai grup).
 * Buang karakter yang bisa merusak sintaks lalu rapikan spasi.
 */
export function sanitizeCustomerSearch(raw: string): string {
  return raw
    .replace(/[(),%*]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Inti nomor HP dari kueri yang terlihat seperti nomor — untuk mencocokkan
 * nomor tersimpan apa pun awalannya (uji nyata S01 T6: nomor yang disalin dari
 * chat WA, mis. "+62 899-0000-1029", dulu "Tidak ada pelanggan cocok" padahal
 * "0899…" ketemu → risiko akun ganda).
 *
 * Hanya angka + pemisah (spasi, strip, titik, kurung, "+" di depan) yang
 * dianggap nomor. Non-angka dibuang, lalu awalan "62" atau "0" dilepas,
 * sehingga "0899…", "62899…", "+62 899…" semuanya menjadi "899…" — cocok
 * dengan nomor tersimpan berawalan 0 maupun 62. Kurang dari 6 digit → null
 * (terlalu umum, pakai pencarian biasa).
 */
export function phoneSearchKey(raw: string): string | null {
  const t = raw.trim();
  if (!/^\+?[\d\s.\-()]+$/.test(t)) return null;
  let digits = t.replace(/\D/g, "");
  if (digits.startsWith("62")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = digits.slice(1);
  return digits.length >= 6 ? digits : null;
}

/**
 * Ekspresi `or` PostgREST untuk mencari nama / nama toko / nomor WA.
 * Mengembalikan null bila query kosong (tak perlu filter). Kueri berbentuk
 * nomor dicocokkan ke kolom `phone` lewat intinya (`phoneSearchKey`).
 */
export function customerSearchOrExpr(raw: string): string | null {
  const q = sanitizeCustomerSearch(raw);
  if (!q) return null;
  const phone = phoneSearchKey(raw) ?? q;
  return `full_name.ilike.%${q}%,store_name.ilike.%${q}%,phone.ilike.%${phone}%`;
}

/**
 * Bentuk minimal query builder Supabase yang dipakai filter. Non-generik
 * (metode mengembalikan interface yang sama) agar tidak memicu "type
 * instantiation excessively deep" saat dipadukan dengan tipe builder Supabase
 * yang sangat generik.
 */
export interface CustomerFilterableQuery {
  eq(column: string, value: unknown): CustomerFilterableQuery;
  in(column: string, values: readonly unknown[]): CustomerFilterableQuery;
  is(column: string, value: unknown): CustomerFilterableQuery;
  not(column: string, operator: string, value: unknown): CustomerFilterableQuery;
  or(filters: string): CustomerFilterableQuery;
}

/**
 * Terapkan filter tab + pencarian ke query builder Supabase. Single-source agar
 * SSR halaman, fetch paginasi, hitung jumlah, dan "pilih semua" memakai logika
 * yang sama persis. Mengembalikan builder yang sama (tipe asli dipertahankan)
 * sehingga `.order()/.range()/.count` tetap bisa dirantai setelahnya.
 *
 * - all       → tanpa filter status
 * - pending   → status = 'pending'
 * - group:<id> → status = 'active' DAN group_id = <id> (satu grup pelanggan spesifik)
 * - no-group  → status = 'active' TANPA grup (Customer biasa)
 * - inactive  → status ∈ {inactive, rejected} (Nonaktif)
 */
export function applyCustomerListFilters<T>(
  query: T,
  opts: { filter: CustomerFilterKey; search: string }
): T {
  let q = query as unknown as CustomerFilterableQuery;
  const groupId = groupIdFromFilterKey(opts.filter);
  if (groupId) {
    q = q.eq("status", "active").eq("group_id", groupId);
  } else {
    switch (opts.filter) {
      case "pending":
        q = q.eq("status", "pending");
        break;
      case "inactive":
        q = q.in("status", ["inactive", "rejected"]);
        break;
      case "no-group":
        q = q.eq("status", "active").is("group_id", null);
        break;
      case "all":
      default:
        break;
    }
  }
  const orExpr = customerSearchOrExpr(opts.search);
  if (orExpr) q = q.or(orExpr);
  return q as unknown as T;
}

// ── Warna badge grup pelanggan & admin handler ──────────────────────────────
// Tujuan: tiap grup pelanggan dan tiap admin handler tampil dengan WARNA badge
// yang berbeda agar mudah dibedakan sekilas di daftar pelanggan. String kelas
// ditulis LITERAL utuh karena Tailwind JIT hanya men-scan literal di source
// (pola sama dengan `categoryTileClass` di homeCatalog.ts).

/** Palet warna badge grup pelanggan, dipetakan per slot grup (slot 1–4). */
export const GROUP_BADGE_CLASSES = [
  "bg-emerald-100 text-emerald-700",
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-cyan-100 text-cyan-700"
];

/**
 * Warna badge grup pelanggan berdasar SLOT (1–4) — deterministik & stabil,
 * jadi level pelanggan yang sama selalu memakai warna yang sama. Slot di luar
 * rentang di-modulo agar tetap valid.
 */
export function groupBadgeClass(slot: number): string {
  const idx =
    Number.isFinite(slot) && slot > 0
      ? (Math.floor(slot) - 1) % GROUP_BADGE_CLASSES.length
      : 0;
  return GROUP_BADGE_CLASSES[idx];
}

// Warna badge admin handler TIDAK lagi tinggal di sini: satu-satunya sumbernya
// kini `handlerBadgeClass` di `src/lib/handlerBadge.ts` (dipakai seluruh panel
// admin + label barcode cetak). `adminBadgeClass`/`ADMIN_BADGE_CLASSES` yang
// dulu ada di file ini dihapus karena jadi palet kedua yang bersaing — admin
// yang sama tampil beda warna antar halaman.

// ── Pesan hasil aksi masal ──────────────────────────────────────────────────

export type CustomerBulkAction = "group" | "handler" | "inactive" | "password" | "delete";

export type CustomerBulkNotice = { text: string; tone: "ok" | "error" };

const BULK_DONE_LABEL: Record<CustomerBulkAction, string> = {
  group: "diperbarui",
  handler: "diperbarui",
  password: "diganti passwordnya",
  inactive: "dinonaktifkan",
  delete: "dihapus",
};

const BULK_KEPT_HINT = " Yang gagal tetap terpilih.";

/**
 * Kalimat hasil aksi masal di /admin/customers. Nada (`tone`) ditentukan dari
 * hitungan, BUKAN dengan mengendus kata "gagal" di teks — dulu pesan galat
 * dari server yang tak memuat kata itu tampil hijau seolah berhasil.
 * Akun yang gagal dibiarkan TERPILIH oleh halaman (mis. langsung Nonaktifkan
 * yang tak bisa dihapus), dan kalimatnya mengatakan itu.
 */
export function customerBulkNotice(
  action: CustomerBulkAction,
  result: { successCount?: number; failedCount?: number; firstError?: string | null }
): CustomerBulkNotice {
  const ok = Math.max(0, Number(result.successCount) || 0);
  const failed = Math.max(0, Number(result.failedCount) || 0);
  const reason = result.firstError?.trim() ? ` ${result.firstError.trim()}` : "";
  if (failed === 0) {
    return { text: `${ok} akun ${BULK_DONE_LABEL[action]}.`, tone: "ok" };
  }
  if (ok === 0) {
    return { text: `Semua gagal (${failed} akun).${reason}${BULK_KEPT_HINT}`, tone: "error" };
  }
  return {
    text: `${ok} akun ${BULK_DONE_LABEL[action]}, ${failed} gagal.${reason}${BULK_KEPT_HINT}`,
    tone: "error",
  };
}
